import { ExportsDataTypes } from "./exportsDataTypes";
import {
  Identifiable,
  List,
  planzone,
  Planzone,
  project,
  Project,
  ProjectReport,
  ResourcesListingFilter,
  TimeSheet,
  WbsFilter,
  Sort,
  Gantt,
  Id,
  Zone,
  Filter,
  ProjectPortfolio,
  StrategicAxis,
  Role,
  ProjectListingConfiguration,
  WbsConfiguration,
  SecondaryCriteriaTemplate,
  ProjectBudgetSynthesis,
  ProjectExpense,
  GanttFilter,
  WbsMeta,
} from "@planzone/dto";

type ExportsData =
  | {
      type: ExportsDataTypes.WBS;
      data: {
        planzone: Identifiable<Planzone>;
        project: Identifiable<Project>;
        wbs: Identifiable<List>[];
        roles: Identifiable<Role>[];
        configuration: WbsConfiguration;
        template: SecondaryCriteriaTemplate;
        filter?: WbsFilter;
      };
    }
  | {
      type: ExportsDataTypes.OPTIMIZED_GANTT_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        projects: Identifiable<Project>[];
        dateRange: [Date, Date];
        toggledLists: { [key: string]: boolean };
        filter?: GanttFilter;
        sort?: Sort;
        ganttType?: "complete" | "simple";
      };
    }
  | {
      type: ExportsDataTypes.OPTIMIZED_WBS_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        projects: Identifiable<Project>[];
        template: SecondaryCriteriaTemplate;
        roles: Identifiable<Role>[];
        configuration: WbsConfiguration;
        toggledLists: { [key: string]: boolean };
        filter?: GanttFilter;
        sort?: Sort;
      };
    }
  | {
      type: ExportsDataTypes.PROJECT_REPORTS;
      data: {
        planzone: Identifiable<Planzone>;
        project: Identifiable<Project>;
        reports: Identifiable<ProjectReport>[];
      };
    }
  | {
      type: ExportsDataTypes.RESOURCES_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        // The planzone resources, or the members of the project when one is given, filtered and sorted
        // like the members pages list them. Page and size are ignored: the whole list is exported.
        project?: Identifiable<Project>;
        filter?: ResourcesListingFilter;
      };
    }
  | {
      type: ExportsDataTypes.PLANZONE_WBS_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        filter?: WbsFilter;
        sort?: Sort[];
        projects: (Identifiable<Project> & {
          lists: Identifiable<List>[];
        })[];
        configuration: WbsConfiguration;
        template: SecondaryCriteriaTemplate;
        roles: Identifiable<Role>[];
      };
    }
  | {
      type: ExportsDataTypes.PROJECT_TIMESHEET_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        projects: {
          project: Identifiable<Project>;
          timesheet: TimeSheet;
        }[];
        startDate: string;
        endDate: string;
        reportType: ("monthly" | "weekly" | "daily")[];
        filter?: WbsFilter;
        sort?: Sort[];
        filterByResourceIntegrationDate: boolean;
        filterByProjectIntegrationDate: boolean;
      };
    }
  | {
      type: ExportsDataTypes.GANTT_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        dateRange: [Date, Date];
        gantt: Gantt;
        toggledLists: Id[];
      };
    }
  | {
      type: ExportsDataTypes.TASK_TIMESHEET_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        projects: {
          project: Identifiable<Project>;
          timesheet: TimeSheet;
        }[];
        startDate: string;
        endDate: string;
        reportType: ("monthly" | "weekly" | "daily")[];
        filter?: WbsFilter;
        sort?: Sort[];
        roles: Identifiable<Role>[];
        configuration: WbsConfiguration;
        template: SecondaryCriteriaTemplate;
        filterByResourceIntegrationDate: boolean;
        filterByProjectIntegrationDate: boolean;
      };
    }
  | {
      type: ExportsDataTypes.ZONES_PROJECTS_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        zones: (Zone & {
          projects: Identifiable<Project>[];
        })[];
        strategicAxis: Identifiable<StrategicAxis>[];
        roles: Identifiable<Role>[];
        configuration: ProjectListingConfiguration;
        template: SecondaryCriteriaTemplate;
        filter: Filter;
        sort: Sort;
      };
    }
  | {
      type: ExportsDataTypes.PROJECT_PORTFOLIOS_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        projectPortfolios: Identifiable<ProjectPortfolio>[];
        strategicAxis: Identifiable<StrategicAxis>[];
        roles: Identifiable<Role>[];
        configuration: ProjectListingConfiguration;
        template: SecondaryCriteriaTemplate;
        filter: Filter;
      };
    }
  | {
      type: ExportsDataTypes.RESOURCES_USAGE_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        startDate: string;
        endDate: string;
      };
    }
  | {
      type: ExportsDataTypes.BUDGET_REPORT;
      data: {
        project: Identifiable<Project>;
        synthesis: ProjectBudgetSynthesis;
        projectExpenses: Identifiable<ProjectExpense>[];
        timeUnit: string;
      };
    }
  | {
      type: ExportsDataTypes.RESOURCE_PROJECT_ALLOCATION_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        startDate: string | null;
        filter?: Filter | undefined;
      };
    }
  | {
      type: ExportsDataTypes.CUSTOM_CPAM_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        year: number;
        filter?: Filter | undefined;
      };
    }
  | {
      type: ExportsDataTypes.RESOURCE_ALLOCATED_TIME_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        startDate: string;
        endDate: string;
      };
    }
  | {
      type: ExportsDataTypes.TASK_BUDGET_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        stopDate: string;
        filter?: WbsFilter;
      };
    }
  | {
      type: ExportsDataTypes.PROJECT_DAILY_PLANNING_REPORT;
      data: {
        planzone: Identifiable<Planzone>;
        project: Identifiable<Project>;
        startDate: string;
        endDate: string;
        columns: string[];
        filter?: GanttFilter;
        sort?: Sort;
        template: SecondaryCriteriaTemplate;
        roles: Identifiable<Role>[];
      };
    };

const exportsData: ExportsData = {
  type: ExportsDataTypes.WBS,
  data: {
    planzone,
    project,
    wbs: [],
    roles: [],
    template: [],
    configuration: [],
  },
};

export type { ExportsData };

export { exportsData };
